/**
 * RewardsTab — Types de coupon + Récompenses catalogue.
 */
import React, { useCallback, useState } from "react";
import {
    Box, Button, Tabs, Tab, Typography, Divider,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AddIcon from "@mui/icons-material/Add";

import { SECTION_COLORS } from "../../constants";
import { fetchRewardForEdit } from "../../utils/rewardCouponTypesApi";

import CouponTypesSection from "./couponTypes/CouponTypesSection";
import CreateCouponTypeDialog from "./couponTypes/CreateCouponTypeDialog";
import EditCouponTypeDialog from "./couponTypes/EditCouponTypeDialog";
import ViewCouponTypeDialog from "./couponTypes/ViewCouponTypeDialog";
import CatalogDeleteDialog, { formatCatalogDeleteStats } from "../shared/CatalogDeleteDialog";

import RewardsListSection from "./RewardsListSection";
import CreateRewardDialog from "./CreateRewardDialog";
import EditRewardDialog from "./EditRewardDialog";
import ViewRewardDialog from "./ViewRewardDialog";

const SUB_TABS = [
    { label: "Types de Coupon", icon: <ConfirmationNumberIcon sx={{ fontSize: 15 }} /> },
    { label: "Récompenses", icon: <CardGiftcardIcon sx={{ fontSize: 15 }} /> },
];

const RewardsTab = ({
    couponTypesApi,
    rewardsApi,
    brandName,
    notify,
}) => {
    const [subTab, setSubTab] = useState(0);

    const [createCouponTypeOpen, setCreateCouponTypeOpen] = useState(false);
    const [viewCouponType, setViewCouponType] = useState(null);
    const [editCouponType, setEditCouponType] = useState(null);
    const [deleteCouponType, setDeleteCouponType] = useState(null);

    const [createRewardOpen, setCreateRewardOpen] = useState(false);
    const [viewReward, setViewReward] = useState(null);
    const [editReward, setEditReward] = useState(null);
    const [deleteReward, setDeleteReward] = useState(null);

    const [pages, setPages] = useState([0, 0]);
    const handlePageChange = (idx) => (_, newPage) =>
        setPages((prev) => { const n = [...prev]; n[idx] = newPage; return n; });

    const addActions = [
        { label: "Ajouter un Type de Coupon", action: () => setCreateCouponTypeOpen(true) },
        { label: "Ajouter une Récompense", action: () => setCreateRewardOpen(true) },
    ];

    const couponTypesList = Array.isArray(couponTypesApi?.data) ? couponTypesApi.data : [];

    const openEditReward = useCallback(async (reward) => {
        if (!reward?.id || !brandName) {
            setEditReward(reward);
            return;
        }
        try {
            const fresh = await fetchRewardForEdit(reward.id, brandName);
            setEditReward(fresh);
        } catch {
            setEditReward(reward);
        }
    }, [brandName]);

    return (
        <>
            <CreateCouponTypeDialog
                open={createCouponTypeOpen}
                onClose={() => setCreateCouponTypeOpen(false)}
                onCreated={() => { couponTypesApi.refetch(); notify("Type de coupon créé avec succès"); }}
            />
            <ViewCouponTypeDialog
                open={Boolean(viewCouponType)} couponType={viewCouponType}
                onClose={() => setViewCouponType(null)} onEditRequest={setEditCouponType}
            />
            <EditCouponTypeDialog
                open={Boolean(editCouponType)} couponType={editCouponType}
                onClose={() => setEditCouponType(null)}
                onSaved={() => { couponTypesApi.refetch(); rewardsApi.refetch(); notify("Type de coupon mis à jour"); }}
            />
            <CatalogDeleteDialog
                open={Boolean(deleteCouponType)}
                resourceType="coupon_type"
                resource={deleteCouponType ? { id: deleteCouponType.id, name: deleteCouponType.name } : null}
                brandName={brandName}
                onClose={() => setDeleteCouponType(null)}
                onDeactivated={() => {
                    couponTypesApi.refetch();
                    notify(`Type de coupon « ${deleteCouponType?.name} » désactivé`);
                }}
                onDeleted={(data) => {
                    couponTypesApi.refetch();
                    const stats = formatCatalogDeleteStats(data);
                    notify(stats
                        ? `Type de coupon supprimé — ${stats}`
                        : `Type de coupon « ${deleteCouponType?.name} » supprimé`);
                }}
            />

            <CreateRewardDialog
                open={createRewardOpen}
                onClose={() => setCreateRewardOpen(false)}
                onCreated={() => { rewardsApi.refetch(); notify("Récompense créée"); }}
            />
            <ViewRewardDialog
                open={Boolean(viewReward)} reward={viewReward}
                onClose={() => setViewReward(null)} onEditRequest={openEditReward}
                couponTypes={couponTypesList}
            />
            <EditRewardDialog
                open={Boolean(editReward)} reward={editReward}
                onClose={() => setEditReward(null)}
                onSaved={() => { rewardsApi.refetch(); notify("Récompense mise à jour"); }}
            />
            <CatalogDeleteDialog
                open={Boolean(deleteReward)}
                resourceType="reward"
                resource={deleteReward ? { id: deleteReward.id, name: deleteReward.name } : null}
                brandName={brandName}
                onClose={() => setDeleteReward(null)}
                onDeactivated={() => {
                    rewardsApi.refetch();
                    notify(`Récompense « ${deleteReward?.name} » désactivée`);
                }}
                onDeleted={(data) => {
                    rewardsApi.refetch();
                    const stats = formatCatalogDeleteStats(data);
                    notify(stats
                        ? `Récompense supprimée — ${stats}`
                        : `Récompense « ${deleteReward?.name} » supprimée`);
                }}
            />

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: 0.5 },
                        "& .MuiTab-root": {
                            textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                            fontSize: 12, minHeight: 36, borderRadius: 2, px: 1.5, color: "#64748b",
                        },
                        "& .Mui-selected": {
                            color: `${SECTION_COLORS.rewards.main} !important`,
                            bgcolor: `${SECTION_COLORS.rewards.main}12`,
                        },
                        "& .MuiTabs-indicator": {
                            height: 2, borderRadius: "2px 2px 0 0", background: SECTION_COLORS.rewards.gradient,
                        },
                    }}
                >
                    {SUB_TABS.map((t, idx) => (
                        <Tab key={idx} label={<Box display="flex" alignItems="center" gap={0.6}>{t.icon}{t.label}</Box>} />
                    ))}
                </Tabs>

                <Button
                    size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={addActions[subTab].action}
                    sx={{
                        textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                        fontSize: 12, borderRadius: 2, background: SECTION_COLORS.rewards.gradient,
                    }}
                >
                    {addActions[subTab].label}
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {subTab === 0 && (
                <CouponTypesSection
                    couponTypesApi={couponTypesApi}
                    page={pages[0]}
                    onPageChange={handlePageChange(0)}
                    onView={setViewCouponType}
                    onEdit={setEditCouponType}
                    onDelete={setDeleteCouponType}
                />
            )}
            {subTab === 1 && (
                <RewardsListSection
                    rewardsApi={rewardsApi}
                    couponTypes={couponTypesList}
                    page={pages[1]}
                    onPageChange={handlePageChange(1)}
                    onView={setViewReward}
                    onEdit={openEditReward}
                    onDelete={setDeleteReward}
                />
            )}
        </>
    );
};

export default RewardsTab;
